// "Does this issue need a WORKFLOW RECORD moved onto a different
// household?" — the decide rail's entry into the record mover.
//
// Since 2026-09-15 this is a thin adapter over fix-shape.ts, which reads
// the fix the DETECTOR declared (issue.fix) and bridges older rows with a
// narrow inference. The history is worth keeping because it is the whole
// reason the declaration exists:
//
// THE DEAD END (2026-09-12). Three detectors recommended `move-household`
// and a fourth asked the same question with no recommendation. The rail
// POSTed the verb to POST /api/cleanup/decide, which has no such case, so
// the recommended button came back 501 `verb_not_wired`. This predicate
// was written to open the picker instead — gated on the verb, plus a
// one-slug allowlist for the detector that recommends nothing.
//
// THE SAME DEAD END, AGAIN (Grant, 2026-09-15). mortgage-loan-wrong-
// household withholds its recommendation when the address does not
// corroborate the name, so its 151 open name-only issues never qualified
// here and showed only dispositions — "Should it move there?" with no way
// to say yes. Adding that slug to the allowlist would have been the fifth
// per-detector patch of one shape. The fix is that the detector now
// DECLARES what it is asking for and this module reads the declaration.
//
// WHAT THIS DOES NOT DO. It does not add a decide verb. The move already
// has a complete, scope-gated, audited, reversible home of its own —
// POST /api/records/:kind/:id/move-household, which carries the record's
// care plans, its Processing records and its client-status row, and undoes
// through POST /api/archive/undo on one bulk action. Re-implementing that
// inside the decide switch would be a second copy of the most
// consequential write in the OS.

import type { MovableKind } from "../../../shared/cleanup/fix";
import { isMovableKind, MOVABLE_KINDS } from "../../../shared/cleanup/fix";
import { resolveFix, type FixIssueLike } from "./fix-shape";

export type RecordRef = { kind: string; id: string; label: string };

/** The four kinds `moveRecordToHousehold` accepts. */
export const MOVABLE_RECORD_KINDS = MOVABLE_KINDS;

export type MovableRecordKind = MovableKind;

export function isMovableRecordKind(v: string): v is MovableRecordKind {
  return isMovableKind(v);
}

export type IssueLike = FixIssueLike;

export interface RecordMoveTarget {
  /** The record that moves. */
  record: RecordRef;
  recordKind: MovableRecordKind;
  /** Where it sits now — excluded from the picker's search results. */
  currentHouseholdId: string | null;
  /**
   * The household the detector suggested, when it had the evidence to
   * suggest one. The picker does NOT pre-select it: choosing the
   * destination is the judgment these issues exist for.
   */
  suggestedHouseholdId: string | null;
}

export function recordMoveTarget(issue: IssueLike): RecordMoveTarget | null {
  const fix = resolveFix(issue);
  if (!fix || fix.kind !== "move-record") return null;
  return {
    record: { kind: fix.record.kind, id: fix.record.id, label: fix.record.label },
    recordKind: fix.record.kind,
    currentHouseholdId: fix.from?.id ?? null,
    suggestedHouseholdId: fix.to?.id ?? null,
  };
}
